/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-boolean-value */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Flex, Hr, Spacer } from '@amzn/stencil-react-components/layout';
import { Label, Text } from '@amzn/stencil-react-components/text';
import {
  LineChart,
  LineDataSet,
  LineType,
  Point,
  Stat,
  StatType,
} from '@amzn/stencil-react-components/data-visualization';
import { Select } from '@amzn/stencil-react-components/form';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import moment from 'moment-timezone';

import { LINE_CHART_LINE_CONFIGS } from '../constants/chartConfiguration';
import { PRODUCTIVITY_HOURS_LOOKBACK } from '../constants/productivityConfiguration';
import { useGlobalContext, useUserDataContext } from '../hooks';
import { Productivity } from '../types/backendService';
import {
  PerformanceSummary,
  WorkItemData,
  WorkItemGroup,
  WorkItemKey,
} from '../types/productivity';

const IconWithTooltip = withTooltip()(IconQuestionCircle);

/**
 * The UI has dropdowns to allow the user to select:
 * 1. "Process Path" (which is taken to mean "Process / Job Function")
 * 2. "Work Item" (which is taken to mean "Job Action / Size Category / Unit Type / LC Level")
 *
 * In order to populate these dropdowns we create a map that allows us to store the list of possible
 * "Work Items" for each "Process Path".
 * In order to add generalization to this use case we split work items into what we refer to as "work item groups".
 *
 * Keys in the map are strings of the format "Process / Job Function" and the values contain the individual work items
 * contained within each group.
 */
const buildWorkItemGroupMap = (workItemKeys: WorkItemKey[]) => {
  const workItemGroupMap = new Map<string, WorkItemGroup>();

  for (const workItemKey of workItemKeys) {
    const workItemGroupKey = {
      learningCurveFamily: workItemKey.learningCurveFamily,
      process: workItemKey.process,
      jobFunction: workItemKey.jobFunction,
    };
    const workItemGroupKeyString = `${workItemGroupKey.process} / ${workItemGroupKey.jobFunction}`;

    let currentWorkItemGroup: WorkItemGroup;
    if (workItemGroupMap.get(workItemGroupKeyString) !== undefined) {
      currentWorkItemGroup = workItemGroupMap.get(workItemGroupKeyString) as WorkItemGroup;
    } else {
      currentWorkItemGroup = {
        key: workItemGroupKey,
        workItems: [],
      };
    }

    currentWorkItemGroup.workItems.push(workItemKey);
    workItemGroupMap.set(workItemGroupKeyString, currentWorkItemGroup);
  }

  return workItemGroupMap;
};

/**
 * Group hourly data from the backend into a map with a unique key for each work item.
 * Keys are strings in the format:
 *  "<process> / <job function> / <job action> / <size category> / <unit type> / <lc level>".
 */
const groupHourlyData = (hourlyWorkItems: Productivity[]): Map<string, WorkItemData> => {
  const workItemMap = new Map<string, WorkItemData>();

  for (const workItem of hourlyWorkItems) {
    const key: WorkItemKey = {
      learningCurveFamily: workItem.learningCurveFamily!,
      process: workItem.processName!,
      jobFunction: workItem.processAttributes!.functionName!,
      jobAction: workItem.processAttributes!.jobAction!,
      unitType: workItem.processAttributes!.unitType!,
      sizeCategory: workItem.processAttributes!.sizeCategory!,
      lcLevel: Number(workItem.learningCurveId!.slice(-1)),
    };
    // eslint-disable-next-line max-len
    const keyString = `${key.process} / ${key.jobFunction} / ${key.jobAction} / ${key.sizeCategory} / ${key.unitType} / Level ${key.lcLevel}`;

    let currentWorkItem: WorkItemData;
    if (workItemMap.get(keyString) !== undefined) {
      currentWorkItem = workItemMap.get(keyString) as WorkItemData;
    } else {
      currentWorkItem = {
        learningCurveFamily: key.learningCurveFamily,
        process: key.process,
        jobFunction: key.jobFunction,
        jobAction: key.jobAction,
        unitType: key.unitType,
        sizeCategory: key.sizeCategory,
        lcLevel: key.lcLevel,
        datapoints: [],
      };
      workItemMap.set(keyString, currentWorkItem);
    }

    currentWorkItem.datapoints.push({
      timeStart: workItem.metricTime!,
      units: workItem.units!,
      millisecondsWorked: workItem.timeWorked!,
    });
  }

  return workItemMap;
};

// eslint-disable-next-line no-empty-pattern
export const HourlyProductivityTab = ({}) => {
  const { t } = useTranslation();
  const { currentWeekProductivity } = useUserDataContext();
  const { userDetails } = useGlobalContext();

  const isHourlyDataLoading = currentWeekProductivity === undefined;
  const hourlyWorkItems = useMemo(() => groupHourlyData(currentWeekProductivity || []), [currentWeekProductivity]);

  /**
   * A map used to split work items into groups in order to better display a large number of
   * work items on the UI using two dropdowns.
   */
  // eslint-disable-next-line arrow-body-style
  const workItemGroupMap: Map<string, WorkItemGroup> = useMemo(() => {
    return buildWorkItemGroupMap(Array.from(hourlyWorkItems.values()));
  }, [hourlyWorkItems]);

  // State variables to read and set the values of the "Process Path" and "Work Item" dropdowns.
  const [selectedProcessPath, setSelectedProcessPath] = useState('');
  const [selectedWorkItem, setSelectedWorkItem] = useState('');

  /**
   * We extract a list of all possible "Work Item Groups" within workItemGroupMap.
   * This list is used to populate the "Process Path" dropdown.
   */
  const workItemGroupOptions: string[] = useMemo(() => Array.from(
    Array.from(workItemGroupMap.values()).map(
      (element) => `${element.key.process} / ${element.key.jobFunction}`,
    ),
  ), [workItemGroupMap]);

  /**
   * Any time the "Process Path" dropdown changes we want update the options in the "Work Item" dropdown appropriately.
   */
  const workItemOptions: string[] = useMemo(() => {
    if (
      isHourlyDataLoading ||
      selectedProcessPath === '' ||
      !workItemGroupMap.has(selectedProcessPath)
    ) {
      return [];
    }
    return [
      t('all'),
      // eslint-disable-next-line arrow-body-style
      ...workItemGroupMap.get(selectedProcessPath)!.workItems.map((element) => {
        return `${element.jobAction} / ${element.sizeCategory} / ${element.unitType} / Level ${element.lcLevel}`;
      }),
    ];
  }, [workItemGroupMap, selectedProcessPath, isHourlyDataLoading]);

  /**
   * Any time the user changes the "Process Path" or "Work Item" dropdown we update a memoizied value
   * that will be used to update the Hourly chart.
   */
  const activeWorkItemData: WorkItemData[] = useMemo(() => {
    const data: WorkItemData[] = [];

    if (hourlyWorkItems.size === 0 || selectedProcessPath === '' || selectedWorkItem === '') {
      return [];
    }

    const [processPath, jobFunction] = selectedProcessPath.split(' / ');

    if (selectedWorkItem === t('all')) {
      for (const workItem of workItemOptions) {
        if (workItem !== t('all')) {
          const [jobAction, sizeCategory, unitType, lcLevel] = workItem.split(' / ');
          const key = `${processPath} / ${jobFunction} / ${jobAction} / ${sizeCategory} / ${unitType} / ${lcLevel}`;

          data.push(hourlyWorkItems.get(key)!);
        }
      }
    } else {
      const [jobAction, sizeCategory, unitType, lcLevel] = selectedWorkItem!.split(' / ');
      const key = `${processPath} / ${jobFunction} / ${jobAction} / ${sizeCategory} / ${unitType} / ${lcLevel}`;

      data.push(hourlyWorkItems.get(key)!);
    }

    return data;
  }, [selectedWorkItem, selectedProcessPath, hourlyWorkItems]);

  /**
   * Any time the active hourly data updates, we in turn summarize the data
   * for display on the "Performancy Summary" section.
   */
  const performanceSummary: PerformanceSummary = useMemo(() => {
    let totalUnits = 0;
    let totalMillisecondsWorked = 0;

    for (const workItem of activeWorkItemData) {
      for (const dataPoint of workItem.datapoints) {
        totalUnits += dataPoint.units;
        totalMillisecondsWorked += dataPoint.millisecondsWorked;
      }
    }

    const hoursWorked = totalMillisecondsWorked / 3600000;

    return {
      units: totalUnits,
      hoursWorked,
      uph: hoursWorked === 0 ? 0 : totalUnits / hoursWorked,
    };
  }, [activeWorkItemData]);

  /**
   * Construct a line chart showing the UPH of one or more work items
   */
  const buildChartForWorkItems = (workItemData: WorkItemData[]): React.ReactElement => {
    if (workItemData.length === 0) {
      return <></>;
    }

    const dataSets: LineDataSet[] = [];

    let maxUph = 0;

    let numLines = 0;
    for (const workItemDatum of workItemData) {
      // eslint-disable-next-line prefer-const
      let dataPoints: Point[] = [];
      for (const datapoint of workItemDatum.datapoints) {
        // calculate UPH
        const y =
          datapoint.millisecondsWorked === 0
            ? 0
            : Math.trunc(datapoint.units / (datapoint.millisecondsWorked / 3600000));

        if (y > maxUph) {
          maxUph = y;
        }

        dataPoints.push({
          x: datapoint.timeStart,
          y,
        });
      }

      dataSets.push({
        color: LINE_CHART_LINE_CONFIGS[numLines].color,
        data: dataPoints,
        // eslint-disable-next-line max-len
        label: `${workItemDatum.process} / ${workItemDatum.jobFunction} / ${workItemDatum.jobAction} / ${workItemDatum.sizeCategory} / ${workItemDatum.unitType} / Level ${workItemDatum.lcLevel}`,
        dataPointShape: LINE_CHART_LINE_CONFIGS[numLines].shape,
        lineType: LineType.Solid,
      });
      // eslint-disable-next-line no-plusplus
      numLines++;
    }

    const lastPeriodEnd = moment()
      .tz(userDetails.locale)
      .startOf('hour');
    // eslint-disable-next-line prefer-const
    let tickValues: number[] = [];
    for (let hourLookback = 1; hourLookback <= PRODUCTIVITY_HOURS_LOOKBACK; hourLookback++) {
      tickValues.unshift(
        lastPeriodEnd
          .clone()
          .subtract(hourLookback, 'hours')
          .unix(),
      );
    }

    return (
      <>
        <LineChart
          dataSets={dataSets}
          height={300}
          shouldFixLabelOverlap={true}
          showBaselineAxis={true}
          showCrosshair={true}
          showDataPoints={true}
          showDependentAxis={true}
          showDependentAxisGridLines={true}
          showLabels={true}
          showLegend={true}
          showTooltip={true}
          baselineAxisTickValues={tickValues}
          dependentAxisTickValues={[
            0,
            Math.trunc(maxUph * 0.25),
            Math.trunc(maxUph * 0.5),
            Math.trunc(maxUph * 0.75),
            maxUph,
          ]}
          dependentAxisFormatter={(tick) => Math.abs(tick)}
          baselineAxisFormatter={(tick) => moment(tick * 1000).format('M/D H:mm')}
          tooltipFormatter={({ x, y }: any) => ({
            x: moment(x * 1000).format(`M/D H:mm [(${userDetails.locale})]`),
            y: `${y} ${t('uphLong')}`,
          })}
        />
      </>
    );
  };

  /**
   * Any time the active hourly data updates we in turn rebuild the chart
   */
  // eslint-disable-next-line arrow-body-style
  const workItemChart: React.ReactElement = useMemo(() => {
    return buildChartForWorkItems(activeWorkItemData);
  }, [activeWorkItemData]);

  /**
   * Initially display the first process path and select "ALL" work items for that process path
   */
  useEffect(() => {
    const workItemGroups = Array.from(workItemGroupMap.keys());

    if (workItemGroups.length > 0) {
      setSelectedProcessPath(workItemGroups[0]);
      setSelectedWorkItem(t('all'));
    }
  }, [workItemGroupMap]);

  let hourlyDataContent;
  /**
   * Note: The check for s(selectedProcessPath == '' || selectedWorkItem == '') && hourlyWorkItems.size > 0)
   * is to prevent a corner case where "No Data" would otherwise show up until activeWorkItemData re-runs
   * when setSelectedProcessPath and setSelectedWorkItem are called in the useEffect block above.
   */
  if (
    isHourlyDataLoading ||
    ((selectedProcessPath === '' || selectedWorkItem === '') && hourlyWorkItems.size > 0)
  ) {
    hourlyDataContent = (
      <Flex flexDirection="row" justifyContent="center" alignItems="center">
        <Spinner showText loadingText={t('loadingHourlyData')} />
      </Flex>
    );
  } else if (activeWorkItemData.length > 0) {
    hourlyDataContent = (
      <>
        <Text fontWeight="light">
          {t('hourlyDataIntro', { hours: PRODUCTIVITY_HOURS_LOOKBACK })}
        </Text>

        <Spacer height={16} />

        <Flex flexDirection="row" justifyContent="space-between">
          <Col gridGap="S200" width="49%">
            <Label htmlFor="process-path-select" id="process-path-select-label">
              <Flex flexDirection="row" justifyContent="left" alignItems="center">
                {`${t('processPath')} / ${t('jobFunction')}`}
                <IconWithTooltip
                  tooltipText={`${t('processPathExplanation')} ${t('jobFunctionExplanation')}`}
                />
              </Flex>
            </Label>
            <Select
              id="process-path-select"
              labelId="process-path-select-label"
              onChange={(processPath) => {
                setSelectedProcessPath(processPath);

                // Change work items
                setSelectedWorkItem(t('all'));
              }}
              options={workItemGroupOptions}
              value={selectedProcessPath}
            />
          </Col>
          <Col gridGap="S200" width="49%">
            <Label htmlFor="work-item-select" id="work-item-select-label">
              <Flex flexDirection="row" justifyContent="left" alignItems="center">
                {t('workItem')}
                <IconWithTooltip tooltipText={t('workItemExplanation')} />
              </Flex>
            </Label>
            <Select
              id="work-item-select"
              labelId="work-item-select-label"
              onChange={setSelectedWorkItem}
              options={workItemOptions}
              value={selectedWorkItem}
            />
          </Col>
        </Flex>

        <Spacer height={32} />
        <Flex flexDirection="row" justifyContent="left" alignItems="center">
          <Text fontWeight="regular">{t('performanceSummary')}</Text>
          <IconWithTooltip tooltipText={t('performanceSummaryTooltip')} />
        </Flex>
        <Hr size="wide" />
        <Spacer height={32} />

        <Flex flexDirection="row" justifyContent="center">
          <Col width="70%">
            <Flex flexDirection="row" justifyContent="space-around">
              <Stat
                type={StatType.Large}
                numeralText={Math.round(performanceSummary.uph).toString()}
                descriptionText={t('uphLong')}
              />
              <Stat
                type={StatType.Large}
                numeralText={performanceSummary.units.toString()}
                descriptionText={t('units')}
              />
              <Stat
                type={StatType.Large}
                numeralText={performanceSummary.hoursWorked.toFixed(1).toString()}
                descriptionText={t('hours')}
              />
            </Flex>
          </Col>
        </Flex>

        <Spacer height={16} />
        <Flex flexDirection="row" justifyContent="left" alignItems="center">
          <Text fontWeight="regular">
            {t('performanceByHour')} {` (${userDetails.locale})`}
          </Text>
          <IconWithTooltip tooltipText={t('performanceByHourTooltip')} />
        </Flex>
        <Hr size="wide" />

        <Spacer height={32} />
        {workItemChart}
      </>
    );
  } else {
    return <Text>{t('noHourlyProductivityData', { hours: PRODUCTIVITY_HOURS_LOOKBACK })}</Text>;
  }

  return hourlyDataContent;
};
